import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';
import { CURRENT_FRONTEND } from '../util/frontendMigration';

// API hook for the legacy vs. new management-frontend migration:
// usage tracking (pageviews/heartbeats/switches), the one-time announcement
// popup state, the recurring feedback prompt and the statistics view.
const useFrontendUsage = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  // Announcement + feedback-prompt flags for the current user. Silent on
  // purpose (called in the background after login); callers handle errors.
  const getStatus = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/frontendUsage/status',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  }, [authCtx, sendRequest]);

  // Background telemetry (pageview/heartbeat). No toast — tracking must never
  // bother the user; callers catch and ignore failures.
  const trackEvent = useCallback(
    async ({ eventType, path, viewTitle }) => {
      return await sendRequest({
        url: '/api/v1/frontendUsage/events',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { frontend: CURRENT_FRONTEND, eventType, path, viewTitle: viewTitle || undefined },
        noAutoStore: true,
      });
    },
    [authCtx, sendRequest]
  );

  // Records an active switch to the new frontend (before navigating there).
  const trackSwitchToNewFrontend = useCallback(
    async ({ path, viewTitle, origin }) => {
      return await sendRequest({
        url: '/api/v1/frontendUsage/switch',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          fromFrontend: CURRENT_FRONTEND,
          toFrontend: 'new',
          path,
          viewTitle: viewTitle || undefined,
          origin,
        },
        noAutoStore: true,
      });
    },
    [authCtx, sendRequest]
  );

  // Marks the announcement popup as seen (persisted per user in the backend).
  const acknowledgeAnnouncement = useCallback(
    async action => {
      try {
        return await sendRequest({
          url: '/api/v1/frontendUsage/announcement',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { action },
          noAutoStore: true,
        });
      } catch (err) {
        toast.error('Speichern der Einstellungen fehlgeschlagen');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Answers or dismisses the recurring "Warum noch das alte Tool?" prompt.
  const sendMigrationFeedback = useCallback(
    async ({ reasons, comment, dismissed, path, viewTitle }) => {
      try {
        return await sendRequest({
          url: '/api/v1/frontendUsage/feedback',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            reasons,
            comment: comment || undefined,
            dismissed: dismissed === true,
            path,
            viewTitle: viewTitle || undefined,
          },
          noAutoStore: true,
        });
      } catch (err) {
        if (dismissed !== true) {
          toast.error('Fehler beim Senden des Feedbacks');
        }
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Aggregated migration statistics (right frontendUsage:statistics:read).
  const getStatistics = useCallback(
    async ({ from, to } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/frontendUsage/statistics',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { from, to },
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Frontend-Migrations-Statistik');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    getStatus,
    trackEvent,
    trackSwitchToNewFrontend,
    acknowledgeAnnouncement,
    sendMigrationFeedback,
    getStatistics,
  };
};

export default useFrontendUsage;
